import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
// ...existing code...
import {
  Box, Tabs, Tab, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, TextField, MenuItem, FormControl, InputLabel, Select, FormHelperText,
  Card, CardContent, Grid, Accordion, AccordionSummary, AccordionDetails, Radio, RadioGroup, FormControlLabel,
  Pagination, InputAdornment, Divider, useTheme, useMediaQuery, TableContainer, Paper, Chip, Autocomplete
} from '@mui/material';
import { Add, Edit, Block, People, Home, Report, Payment, ExpandMore, Delete, Search, Remove, AccountBalance, CreditCard, Money } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usersAPI, flatsAPI, complaintsAPI, paymentsAPI, buildingsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import CCAvPaymentCard from '../components/CCAvPaymentCard';

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) return resolve(true);
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && (
      <Box sx={{ 
        pt: { xs: 1, sm: 2 }, 
        px: { xs: 1, sm: 1 },
        pb: { xs: 2, sm: 1 }
      }}>
        {children}
      </Box>
    )}
  </div>
);

const UserDialog = ({
  open,
  onClose,
  onSubmit,
  flats,
  buildingIds,
  editingUser,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  
  const userTypes = [
    { value: 'OWNER', label: t('buildingAdmin.users.owner') },
    { value: 'TENANT', label: t('buildingAdmin.users.tenant') },
    { value: 'MAINTENANCE_STAFF', label: t('buildingAdmin.users.maintenanceStaff') },
  ];
  
  const [formData, setFormData] = useState(() => {
    // Handle both flatIds (array) and flatId (single) from API response
    let userFlats = [];
    if (editingUser?.flatIds && Array.isArray(editingUser.flatIds)) {
      userFlats = editingUser.flatIds;
    } else if (editingUser?.flatId) {
      userFlats = [editingUser.flatId];
    } else if (editingUser?.flats && Array.isArray(editingUser.flats)) {
      userFlats = editingUser.flats.map(flat => flat.flatId || flat);
    }
    
    return {
      buildingIds: buildingIds || [],
      firstName: editingUser?.firstName || '',
      lastName: editingUser?.lastName || '',
      email: editingUser?.email || '',
      phoneNumber: editingUser?.phoneNumber || '',
      userType: editingUser?.userType || 'OWNER',
      flatIds: userFlats,
    };
  });

  useEffect(() => {
    if (editingUser) {
      // Handle both flatIds (array) and flatId (single) from API response
      let userFlats = [];
      if (editingUser.flatIds && Array.isArray(editingUser.flatIds)) {
        userFlats = editingUser.flatIds;
      } else if (editingUser.flatId) {
        userFlats = [editingUser.flatId];
      } else if (editingUser.flats && Array.isArray(editingUser.flats)) {
        userFlats = editingUser.flats.map(flat => flat.flatId || flat);
      }
      
      setFormData({
        buildingIds: editingUser.buildingIds || buildingIds || [],
        firstName: editingUser.firstName || '',
        lastName: editingUser.lastName || '',
        email: editingUser.email || '',
        phoneNumber: editingUser.phoneNumber || '',
        userType: editingUser.userType || 'OWNER',
        flatIds: userFlats,
      });
    } else {
      setFormData({
        buildingIds: buildingIds,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        userType: 'OWNER',
        flatIds: [],
      });
    }
  }, [editingUser, open]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingUser) {
      // Edit mode: update user
      await onSubmit({
        ...formData,
        userId: editingUser?.userId,
        buildingIds: Array.isArray(formData.buildingIds) ? formData.buildingIds : [formData.buildingIds]
      });
    } else {
      // Add mode: create user
      await onSubmit({ ...formData});
    }
  };

  return (
    <Dialog className="complaint-dialog" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingUser ? t('buildingAdmin.users.editUser') : t('buildingAdmin.users.addUser')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label={t('buildingAdmin.users.firstName')}
              value={formData.firstName}
              onChange={handleChange('firstName')}
              required
              fullWidth
            />
            <TextField
              label={t('buildingAdmin.users.lastName')}
              value={formData.lastName}
              onChange={handleChange('lastName')}
              required
              fullWidth
            />
            <TextField
              label={t('buildingAdmin.users.email')}
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              required
              fullWidth
            />
            <TextField
              label={t('buildingAdmin.users.phone')}
              value={formData.phoneNumber}
              onChange={handleChange('phoneNumber')}
              required
              fullWidth
            />
            <TextField
              select
              label={t('buildingAdmin.users.userType')}
              value={formData.userType}
              onChange={handleChange('userType')}
              required
              fullWidth
            >
              {userTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('buildingAdmin.users.selectFlats')}
              value={formData.flatIds}
              onChange={e => setFormData(prev => ({ ...prev, flatIds: e.target.value }))}
              SelectProps={{ multiple: true }}
              required={formData.userType === 'OWNER' || formData.userType === 'TENANT'}
              fullWidth
              margin="normal"
              disabled={!buildingIds || buildingIds.length === 0}
            >
              {(flats || []).map(f => (
                <MenuItem key={f.flatId} value={f.flatId}>
                  {f.flatNumber} {f.blockName ? `(${f.blockName})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions className="mobile-sticky-actions">
          <Button onClick={onClose}>{t('buildingAdmin.users.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {editingUser ? t('buildingAdmin.users.save') : t('buildingAdmin.users.addUser')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const FlatDialog = ({
  open,
  onClose,
  onSubmit,
  buildingId,
  blocks,
  //flats,
  editingFlat,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  
  const flatTypes = [
    { value: '1', label: t('buildingAdmin.flats.bhk1') },
    { value: '2', label: t('buildingAdmin.flats.bhk2') },
    { value: '3', label: t('buildingAdmin.flats.bhk3') },
    { value: '4', label: t('buildingAdmin.flats.bhk4') },
    { value: '5', label: t('buildingAdmin.flats.bhk5') },
  ];
  
  const [formData, setFormData] = useState({
    buildingId: '',
    flatId: '',
    flatNumber: '',
    blockId: '',
    blockName: '',
    flatType: '',
    floorNumber: '',
    area: '',
    monthlyMaintenance: '',
    outstandingBalance: '',
    vehicles: []
  });
  useEffect(() => {
    if (editingFlat) {
      console.log('EditingFlat data:', editingFlat); // Debug log
      
      // Convert legacy single vehicle data to array format if needed
      let vehicles = [];
      if (editingFlat.vehicles && Array.isArray(editingFlat.vehicles)) {
        // Ensure each vehicle has an id for form management
        vehicles = editingFlat.vehicles.map((vehicle, index) => ({
          id: vehicle.id || Date.now() + index,
          vehicleType: vehicle.vehicleType || '',
          vehicleNumber: vehicle.vehicleNumber || '',
          fastTagNumber: vehicle.fastTagNumber || '',
          vehicleBrand: vehicle.vehicleBrand || '',
          vehicleModel: vehicle.vehicleModel || ''
        }));
      } else if (editingFlat.vehicleType || editingFlat.vehicleNumber) {
        // Convert legacy format to new format
        vehicles = [{
          id: Date.now(),
          vehicleType: editingFlat.vehicleType || '',
          vehicleNumber: editingFlat.vehicleNumber || '',
          fastTagNumber: editingFlat.fastTagNumber || '',
          vehicleBrand: editingFlat.vehicleBrand || '',
          vehicleModel: editingFlat.vehicleModel || ''
        }];
      }
      
      console.log('Processed vehicles:', vehicles); // Debug log
      
      setFormData({
        buildingId: editingFlat.buildingId || buildingId,
        flatId: editingFlat.flatId || '',
        flatNumber: editingFlat.flatNumber || '',
        blockId: editingFlat.blockId || '',
        blockName: editingFlat.blockName || '',
        flatType: editingFlat.flatType || '',
        floorNumber: editingFlat.floorNumber || '',
        area: editingFlat.area || '',
        monthlyMaintenance: editingFlat.monthlyMaintenance || '',
        outstandingBalance: editingFlat.outstandingBalance?.toString() || '0',
        vehicles: vehicles
      });
    } else {
      setFormData({
        buildingId: buildingId || '',
        flatNumber: '',
        blockId: '',
        blockName: '',
        flatType: '',
        floorNumber: '',
        area: '',
        monthlyMaintenance: '',
        outstandingBalance: '',
        vehicles: []
      });
    }
  }, [editingFlat, open]);

  const handleChange = (field) => (e) => {
    if (field === 'blockId') {
      const selectedBlock = blocks.find(b => b.blockId === e.target.value);
      setFormData(prev => ({
        ...prev,
        blockId: e.target.value,
        blockName: selectedBlock ? selectedBlock.blockName : ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: e.target.value }));
    }
  };

  // Vehicle management functions
  const addVehicle = () => {
    const newVehicle = {
      id: Date.now(),
      vehicleType: '',
      vehicleNumber: '',
      fastTagNumber: '',
      vehicleBrand: '',
      vehicleModel: ''
    };
    setFormData(prev => ({
      ...prev,
      vehicles: [...prev.vehicles, newVehicle]
    }));
  };

  const removeVehicle = (vehicleId) => {
    setFormData(prev => ({
      ...prev,
      vehicles: prev.vehicles.filter(vehicle => vehicle.id !== vehicleId)
    }));
  };

  const updateVehicle = (vehicleId, field, value) => {
    setFormData(prev => ({
      ...prev,
      vehicles: prev.vehicles.map(vehicle =>
        vehicle.id === vehicleId
          ? { ...vehicle, [field]: value }
          : vehicle
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submission data:', formData); // Debug log
    console.log('Vehicles being submitted:', formData.vehicles); // Debug log
    onSubmit(formData);
  };

  return (
    <Dialog className="complaint-dialog" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingFlat ? t('buildingAdmin.flats.editFlat') : t('buildingAdmin.flats.addFlat')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label={t('buildingAdmin.flats.flatNumber')}
              value={formData.flatNumber}
              onChange={handleChange('flatNumber')}
              required
              fullWidth
            />
            <TextField
              select
              label={t('buildingAdmin.flats.block')}
              value={formData.blockId}
              onChange={handleChange('blockId')}
              required
              fullWidth
              margin="normal"
            >
              <MenuItem value="">{t('buildingAdmin.flats.selectBlock')}</MenuItem>
              {(blocks || []).map(block => (
                <MenuItem key={block.blockId} value={block.blockId}>
                  {block.blockName || block.blockId}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('buildingAdmin.flats.flatType')}
              value={formData.flatType}
              onChange={handleChange('flatType')}
              required
              fullWidth
            >
              <MenuItem value="">{t('buildingAdmin.flats.selectFlatType')}</MenuItem>
              {flatTypes.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('buildingAdmin.flats.floorNumber')}
              type="number"
              value={formData.floorNumber}
              onChange={handleChange('floorNumber')}
              required
              fullWidth
            />
            <TextField
              label={t('buildingAdmin.flats.area')}
              type="number"
              value={formData.area}
              onChange={handleChange('area')}
              required
              fullWidth
            />
            <TextField
              label={t('buildingAdmin.flats.monthlyMaintenance')}
              type="number"
              value={formData.monthlyMaintenance}
              onChange={handleChange('monthlyMaintenance')}
              required
              fullWidth
            />
            <TextField
              label={t('buildingAdmin.flats.outstandingBalance')}
              type="number"
              value={formData.outstandingBalance}
              onChange={handleChange('outstandingBalance')}
              fullWidth
            />
            
            {/* Vehicle Details Section */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('buildingAdmin.flats.vehicleDetails')}</Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addVehicle}
                  size="small"
                >
                  {t('buildingAdmin.flats.addVehicle')}
                </Button>
              </Box>
              
              {formData.vehicles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  {t('buildingAdmin.flats.noVehiclesAdded')}
                </Typography>
              ) : (
                formData.vehicles.map((vehicle, index) => (
                  <Box key={vehicle.id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" color="primary">
                        {t('buildingAdmin.flats.vehicleDisplayNumber', { number: index + 1 })}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeVehicle(vehicle.id)}
                        title={t('buildingAdmin.flats.removeVehicle')}
                      >
                        <Remove />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      <TextField
                        select
                        label={t('buildingAdmin.flats.vehicleType')}
                        value={vehicle.vehicleType}
                        onChange={(e) => updateVehicle(vehicle.id, 'vehicleType', e.target.value)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="">{t('buildingAdmin.flats.selectVehicleType')}</MenuItem>
                        <MenuItem value="Car">{t('buildingAdmin.flats.vehicleTypes.car')}</MenuItem>
                        <MenuItem value="Motorcycle">{t('buildingAdmin.flats.vehicleTypes.motorcycle')}</MenuItem>
                        <MenuItem value="Scooter">{t('buildingAdmin.flats.vehicleTypes.scooter')}</MenuItem>
                        <MenuItem value="SUV">{t('buildingAdmin.flats.vehicleTypes.suv')}</MenuItem>
                        <MenuItem value="Other">{t('buildingAdmin.flats.vehicleTypes.other')}</MenuItem>
                      </TextField>
                      
                      <TextField
                        label={t('buildingAdmin.flats.vehicleNumber')}
                        value={vehicle.vehicleNumber}
                        onChange={(e) => updateVehicle(vehicle.id, 'vehicleNumber', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="e.g., KA01AB1234"
                      />
                      
                      <TextField
                        label={t('buildingAdmin.flats.fastTagNumber')}
                        value={vehicle.fastTagNumber}
                        onChange={(e) => updateVehicle(vehicle.id, 'fastTagNumber', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="e.g., 1234567890123456"
                      />
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                          label={t('buildingAdmin.flats.vehicleBrand')}
                          value={vehicle.vehicleBrand}
                          onChange={(e) => updateVehicle(vehicle.id, 'vehicleBrand', e.target.value)}
                          fullWidth
                          size="small"
                          placeholder="e.g., Maruti, Honda"
                        />
                        
                        <TextField
                          label={t('buildingAdmin.flats.vehicleModel')}
                          value={vehicle.vehicleModel}
                          onChange={(e) => updateVehicle(vehicle.id, 'vehicleModel', e.target.value)}
                          fullWidth
                          size="small"
                          placeholder="e.g., Swift, City"
                        />
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className="mobile-sticky-actions">
          <Button onClick={onClose}>{t('buildingAdmin.flats.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {editingFlat ? t('buildingAdmin.flats.update') : t('buildingAdmin.flats.add')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const ComplaintDialog = ({
  open,
  onClose,
  onSubmit,
  editingComplaint,
  isLoading,
  error,
  buildingIds,
  flats,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    buildingId: editingComplaint?.buildingId || '',
    title: editingComplaint?.title || '',
    description: editingComplaint?.description || '',
    category: editingComplaint?.category || '',
    status: editingComplaint?.status || 'OPEN',
    priority: editingComplaint?.priority || 'MEDIUM',
    flatId: editingComplaint?.flatId || '',
  });

  useEffect(() => {
    if (editingComplaint) {
      console.log('Setting form data with editingComplaint:', editingComplaint);
      console.log('Setting category to:', editingComplaint.category || '');
      console.log('Setting priority to:', editingComplaint.priority || 'MEDIUM');
      setFormData({
        buildingId: editingComplaint.buildingId || '',
        title: editingComplaint.title || '',
        description: editingComplaint.description || '',
        category: editingComplaint.category || '',
        status: editingComplaint.status || 'OPEN',
        priority: editingComplaint.priority || 'MEDIUM',
        flatId: editingComplaint.flatId || '',
      });
    } else {
      console.log('Resetting form data for new complaint');
      setFormData({
        buildingId: buildingIds[0] || '',
        title: '',
        description: '',
        category: '',
        status: 'OPEN',
        priority: 'MEDIUM',
        flatId: '',
      });
    }
  }, [editingComplaint, open, buildingIds]);

  const handleChange = (field) => (e) => {
    console.log(`Field ${field} changed to:`, e.target.value);
    setFormData((prev) => {
      const newData = { ...prev, [field]: e.target.value };
      console.log('Updated form data:', newData);
      return newData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog className="complaint-dialog" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingComplaint ? t('buildingAdmin.complaints.editComplaint') : t('buildingAdmin.complaints.addComplaint')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label={t('buildingAdmin.complaints.title')}
              value={formData.title}
              onChange={handleChange('title')}
              required
              fullWidth
            />
            <TextField
              label={t('buildingAdmin.complaints.description')}
              value={formData.description}
              onChange={handleChange('description')}
              required
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label={t('buildingAdmin.complaints.category')}
              value={formData.category}
              onChange={(e) => {
                console.log('Category changed to:', e.target.value);
                handleChange('category')(e);
              }}
              required
              fullWidth
            >
              <MenuItem value="PLUMBING">{t('buildingAdmin.complaints.categoryPlumbing')}</MenuItem>
              <MenuItem value="ELECTRICAL">{t('buildingAdmin.complaints.categoryElectrical')}</MenuItem>
              <MenuItem value="CLEANING">{t('buildingAdmin.complaints.categoryCleaning')}</MenuItem>
              <MenuItem value="SECURITY">{t('buildingAdmin.complaints.categorySecurity')}</MenuItem>
              <MenuItem value="MAINTENANCE">{t('buildingAdmin.complaints.categoryMaintenance')}</MenuItem>
              <MenuItem value="OTHER">{t('buildingAdmin.complaints.categoryOther')}</MenuItem>
            </TextField>
            <TextField
              select
              label={t('buildingAdmin.complaints.status')}
              value={formData.status}
              onChange={handleChange('status')}
              required
              fullWidth
            >
              <MenuItem value="OPEN">{t('buildingAdmin.complaints.statusOpen')}</MenuItem>
              <MenuItem value="IN_PROGRESS">{t('buildingAdmin.complaints.statusInProgress')}</MenuItem>
              <MenuItem value="RESOLVED">{t('buildingAdmin.complaints.statusResolved')}</MenuItem>
              <MenuItem value="CLOSED">{t('buildingAdmin.complaints.statusClosed')}</MenuItem>
            </TextField>
            <TextField
              select
              label={t('buildingAdmin.complaints.priority')}
              value={formData.priority}
              onChange={(e) => {
                console.log('Priority changed to:', e.target.value);
                handleChange('priority')(e);
              }}
              required
              fullWidth
            >
              <MenuItem value="LOW">{t('buildingAdmin.complaints.priorityLow')}</MenuItem>
              <MenuItem value="MEDIUM">{t('buildingAdmin.complaints.priorityMedium')}</MenuItem>
              <MenuItem value="HIGH">{t('buildingAdmin.complaints.priorityHigh')}</MenuItem>
            </TextField>
            <TextField
              select
              label={t('buildingAdmin.complaints.flat')}
              value={formData.flatId}
              onChange={handleChange('flatId')}
              required
              fullWidth
            >
              <MenuItem value="">-- Select Flat --</MenuItem>
              {(flats || []).map(f => (
                <MenuItem key={f.flatId} value={f.flatId}>
                  {f.flatNumber} {f.blockName ? `(${f.blockName})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions className="mobile-sticky-actions">
          <Button onClick={onClose}>{t('buildingAdmin.complaints.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {editingComplaint ? t('buildingAdmin.complaints.update') : t('buildingAdmin.complaints.add')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Card sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      bgcolor: color, 
      color: 'white', 
      minHeight: { xs: 64, sm: 84 },
      p: { xs: 0.25, sm: 0.5 },
      borderRadius: 1
    }}>
      <CardContent sx={{ 
        flex: 1, 
        p: { xs: 0.5, sm: 1 },
        '&:last-child': { pb: { xs: 0.5, sm: 1 } }
      }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontSize: { xs: '11px', sm: '12px' },
            mb: { xs: 0.25, sm: 0.5 },
            opacity: 0.95
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="h5" 
          sx={{ 
            fontSize: { xs: '15px', sm: '18px', md: '20px' },
            fontWeight: 700,
            lineHeight: 1.1
          }}
        >
          {value}
        </Typography>
      </CardContent>
      <Box sx={{ pr: { xs: 0.5, sm: 1 } }}>{icon}</Box>
    </Card>
  );
};

const BuildingAdminDashboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Payments Report tab: selected flat for dropdown
  const [selectedFlatId, setSelectedFlatId] = useState('');
  useEffect(() => {
    // Listen for flatDropdownChange event (for compatibility with previous patch)
    const handler = () => setSelectedFlatId(window.selectedFlatId || '');
    window.addEventListener('flatDropdownChange', handler);
    return () => window.removeEventListener('flatDropdownChange', handler);
  }, []);

  // All useState hooks must come first!
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [payments, setPayments] = useState([]); // for selected month
  const [paymentsStats, setPaymentsStats] = useState([]); // for statistics (last 6 months)
  const [paymentsRefreshTrigger, setPaymentsRefreshTrigger] = useState(0);
  
  // Payment state
  const [selectedFlatForPayment, setSelectedFlatForPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [paymentConfirmationType, setPaymentConfirmationType] = useState('');
  const [paymentConfirmationMessage, setPaymentConfirmationMessage] = useState('');
  const [ccavenuePaymentAmount, setCcavenuePaymentAmount] = useState({});
  const [paymentError, setPaymentError] = useState('');
  const [flatFilter, setFlatFilter] = useState('pending'); // 'all', 'pending', 'completed' - default to pending

  // Payment gateway states for Razorpay and CCAvenue
  const [payingOutstanding, setPayingOutstanding] = useState(false);
  const [paymentGateway, setPaymentGateway] = useState('Cash'); // 'Cash', 'Razorpay', 'CCAvenue'
  const [outstandingError, setOutstandingError] = useState('');
  const [outstandingSuccess, setOutstandingSuccess] = useState(false);
  
  // CCAvenue payment card state
  const [showCCAvCardMap, setShowCCAvCardMap] = useState({});  // Track per flat

  // Pagination and search states for payments
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPageSize, setPaymentsPageSize] = useState(10);
  const [flatSearchTerm, setFlatSearchTerm] = useState('');
  
  // Date range state for payments (default: last 45 days)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 45);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Function to refresh flats data from backend
  const refreshFlatsData = async () => {
    if (buildingIds && buildingIds.length > 0) {
      try {
        const res = await flatsAPI.getByBuilding(buildingIds[0]);
        setFlats(res.data);
        // Clear cached flat details when refreshing flats data
        setFlatDetailsMap({});
        setFlatPaymentsMap({});
        setExpandedFlatId(null);
        setFlatLoadingMap({});
      } catch (error) {
        console.error('Failed to refresh flats data:', error);
      }
    }
  };
  const [paymentsError, setPaymentsError] = useState('');
  // Users state
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Users pagination and search
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);
  const [usersSearchTerm, setUsersSearchTerm] = useState('');

  // Flats state
  const [flats, setFlats] = useState([]);
  const [flatsError, setFlatsError] = useState('');
  const [flatDialogOpen, setFlatDialogOpen] = useState(false);
  const [editingFlat, setEditingFlat] = useState(null);
  
  // Flat pagination state (search term is declared above for payments)
  const [currentPage, setCurrentPage] = useState(1);
  const [flatsPageSize, setFlatsPageSize] = useState(10);

  // FLATS tab: lazy load payments and details for a flat only when selected (accordion expanded)
  const [flatPaymentsMap, setFlatPaymentsMap] = useState({});
  const [flatDetailsMap, setFlatDetailsMap] = useState({});
  const [expandedFlatId, setExpandedFlatId] = useState(null);
  const [flatLoadingMap, setFlatLoadingMap] = useState({});

  const handleAccordionChange = (flatId) => (event, isExpanded) => {
    setExpandedFlatId(isExpanded ? flatId : null);
    if (isExpanded && !flatDetailsMap[flatId]) {
      setFlatLoadingMap(prev => ({ ...prev, [flatId]: true }));
      (async () => {
        try {
          const flat = flats.find(f => f.flatId === flatId);
          
          // Load comprehensive flat details (includes payment history and vehicle data)
          const flatDetailsRes = await flatsAPI.getDetails(flatId, flat.flatNumber);
          const flatDetails = flatDetailsRes.data;
          
          // Store flat details
          setFlatDetailsMap(prev => ({ ...prev, [flatId]: flatDetails }));
          
          // Store payment history from flat details for backward compatibility
          if (flatDetails.completedPaymentHistory && Array.isArray(flatDetails.completedPaymentHistory)) {
            setFlatPaymentsMap(prev => ({ ...prev, [flatId]: flatDetails.completedPaymentHistory }));
          }
        } catch (err) {
          console.error('Error loading flat details:', err);
        }
        setFlatLoadingMap(prev => ({ ...prev, [flatId]: false }));
      })();
    }
  };
  const { user } = useAuth();
  
  // Authorization logic - similar to Dashboard.js
  const allowedGroups = ["admin", "building_admin"];
  const userGroups = user?.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check both groups and direct role property
  const hasAccess = groupNames.some(g => allowedGroups.includes(g)) || 
                   allowedGroups.includes(user?.role);

  const [tab, setTab] = useState(0);
  const [buildingIds, setBuildingIds] = useState([]);
  const [buildingName, setBuildingName] = useState('');
  const [isLoadingAssociations, setIsLoadingAssociations] = useState(true);

  // Complaints state
  const [complaints, setComplaints] = useState([]);
  const [complaintsError, setComplaintsError] = useState('');
  const [complaintDialogOpen, setComplaintDialogOpen] = useState(false);
  const [editingComplaint, setEditingComplaint] = useState(null);
  
  // Complaints pagination and filtering
  const [complaintsPage, setComplaintsPage] = useState(1);
  const [complaintsPageSize, setComplaintsPageSize] = useState(10);
  const [complaintsStartDate, setComplaintsStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to 30 days ago
    return date.toISOString().split('T')[0];
  });
  const [complaintsEndDate, setComplaintsEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Blocks state
  const [blocks, setBlocks] = useState([]);

  // Payment methods state
  const [paymentMethodSelections, setPaymentMethodSelections] = useState({});

  // Building summary statistics state
  const [buildingSummary, setBuildingSummary] = useState({
    flatsWithPendingPayments: 0,
    monthlyRevenue: 0,
    monthlyReceivables: 0,
    totalFlats: 0,
    totalOutstanding: 0
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  // Place calculation after all useState hooks
  // Calculate number of flats with pending payments (outstanding balance > 0) - fallback for when summary is not loaded
  const flatsWithPendingPayments = buildingSummary.flatsWithPendingPayments > 0 ? 
    [] : (flats || []).filter(flat => {
      const outstandingBalance = Number(flat.outstandingBalance) || 0;
      return outstandingBalance > 0;
    });

  // Fetch building associations for the user
  useEffect(() => {
    const fetchAssociations = async () => {
      if (!user) {
        setIsLoadingAssociations(false);
        return;
      }
      const userId = user.userId || user.attributes?.sub || user.username;
      try {
        const res = await usersAPI.getAssociations(userId);
        // Collect buildingIds and building name from association results
        const buildingAssociations = res.data || [];
        
        const ids = buildingAssociations.map(item => item.buildingId);
        setBuildingIds(ids);
        
        // Set building name from the first building association
        if (buildingAssociations.length > 0) {
          const firstBuilding = buildingAssociations[0];
          setBuildingName(firstBuilding.buildingName || 'Building');
        }
        console.log('Building Associations:', buildingAssociations);
        console.log('Building IDs:', ids);
        if (ids.length !== 0) {
          const buildingId = ids[0];
          // Only fetch blocks initially since they're needed for flat creation
          // Other data will be loaded when tabs are accessed
          buildingsAPI.getById(buildingId)
            .then(res => setBlocks(res.data?.blocks || []))
            .catch(() => setBlocks([]));
        }        
      } catch (err) {
        setBuildingIds([]);
        setBuildingName('');
        setBlocks([]);
      } finally {
        setIsLoadingAssociations(false);
      }
    };
    fetchAssociations();
  }, [user]);

  // Fetch building summary statistics (efficient API call)
  useEffect(() => {
    if (buildingIds.length === 0) return;
    const buildingId = buildingIds[0];
    
    setSummaryLoading(true);
    setSummaryError('');
    
    // Fetch building summary for dashboard statistics using paymentsAPI
    paymentsAPI.getBuildingSummary(buildingId)
      .then(res => {
        const data = res.data;
        setBuildingSummary({
          flatsWithPendingPayments: data.flatsWithPendingPayments || 0,
          monthlyRevenue: data.monthlyRevenue || 0,
          monthlyReceivables: data.monthlyReceivables || 0,
          totalFlats: data.totalFlats || 0,
          totalOutstanding: data.totalOutstanding || 0
        });
      })
      .catch(error => {
        console.error('Failed to fetch building summary:', error);
        setSummaryError('Failed to load building statistics');
        // Fallback: calculate basic stats from flats data if available
        if (flats && flats.length > 0) {
          const flatsWithPending = flats.filter(flat => Number(flat.outstandingBalance) > 0).length;
        const monthlyRev = flats.reduce((sum, flat) => sum + (Number(flat.monthlyMaintenance) || 0), 0);
        const totalOutstand = flats.reduce((sum, flat) => sum + (Number(flat.outstandingBalance) || 0), 0);
        setBuildingSummary({
          flatsWithPendingPayments: flatsWithPending,
          monthlyRevenue: monthlyRev,
          monthlyReceivables: 0, // Cannot calculate without payments data
          totalFlats: flats.length,
          totalOutstanding: totalOutstand
        });
      }
    })
    .finally(() => setSummaryLoading(false));
  }, [buildingIds, paymentsRefreshTrigger]);

  // Fetch payments data for table display only (when payments tab is accessed)
  useEffect(() => {
    if (buildingIds.length === 0 || tab !== 3) return; // Only load when payments tab is active
    const buildingId = buildingIds[0];
    
    paymentsAPI.getByBuilding(buildingId)
      .then(res => {
        const allPayments = Array.isArray(res.data) ? res.data : [];
        
        // Filter payments by date range
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Include full end date
        
        const filteredPayments = allPayments.filter(payment => {
          // Always include Outstanding payments
          if (payment.paymentType === 'Outstanding') {
            return true;
          }
          
          // Filter by date range for other payments
          let paymentDate;
          if (payment.paidDate) {
            paymentDate = new Date(payment.paidDate);
          } else if (payment.dueDate) {
            paymentDate = new Date(payment.dueDate);
          } else if (payment.year && payment.month) {
            paymentDate = new Date(payment.year, payment.month - 1);
          } else {
            // If no date information, include it
            return true;
          }
          
          return paymentDate >= start && paymentDate <= end;
        });
        
        // Set filtered payments for table display
        setPayments(filteredPayments);
        
        // For statistics, use last 6 months from current date (not user-selected range)
        const now = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        
        const statsPayments = allPayments.filter(payment => {
          if (payment.paymentType === 'Outstanding' || !payment.year || !payment.month) {
            return true;
          }
          
          const paymentDate = new Date(payment.year, payment.month - 1);
          return paymentDate >= sixMonthsAgo;
        });
        
        setPaymentsStats(statsPayments);
      })
      .catch(() => setPaymentsError('Failed to load payments'));
  }, [buildingIds, paymentsRefreshTrigger, startDate, endDate, tab]);

  // Load tab data as needed
  React.useEffect(() => {
    if (buildingIds.length === 0) return;
    const buildingId = buildingIds[0];
    
    if (tab === 0) {
      // Users tab - load users data
      usersAPI.getUsers(buildingId)
        .then(res => setUsers(res.data))
        .catch(() => setUsersError('Failed to load users'));
    } else if (tab === 2) {
      // Complaints tab - load complaints data (already loaded on initial render)
      console.log('Complaints tab selected - data already loaded');
    }
  }, [buildingIds, tab]);

  // Load complaints data with date filtering
  React.useEffect(() => {
    if (buildingIds.length === 0) return;
    const buildingId = buildingIds[0];
    
    // Load complaints data - needed for statistics section and filtered for date range
    console.log('Loading complaints for statistics, buildingId:', buildingId);
    complaintsAPI.getByBuilding(buildingId)
      .then(res => {
        console.log('Complaints API response:', res);
        console.log('Complaints data:', res.data);
        // Handle both possible response structures
        let complaintsData = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
        
        // Apply date filtering
        const startDate = new Date(complaintsStartDate);
        const endDate = new Date(complaintsEndDate);
        endDate.setHours(23, 59, 59, 999); // Include end of day
        
        complaintsData = complaintsData.filter(complaint => {
          if (!complaint.createdAt) return true; // Include complaints without date
          const createdDate = new Date(complaint.createdAt);
          return createdDate >= startDate && createdDate <= endDate;
        });
        
        console.log('Final filtered complaints data:', complaintsData);
        setComplaints(complaintsData);
        setComplaintsPage(1); // Reset to first page when data changes
      })
      .catch((err) => {
        console.error('Failed to load complaints:', err);
        setComplaintsError('Failed to load complaints');
      });
  }, [buildingIds, complaintsStartDate, complaintsEndDate]);

  // Load flats data when needed by any tab (once per building)
  React.useEffect(() => {
    if (buildingIds.length === 0) return;
    const buildingId = buildingIds[0];
    
    // Load flats data - needed by multiple tabs
    flatsAPI.getByBuilding(buildingId)
      .then(res => setFlats(res.data))
      .catch(() => setFlatsError('Failed to load flats'));
  }, [buildingIds]);

  // Fetch blocks for the building
  /* useEffect(() => {
    if (buildingIds.length === 0) return;
    const buildingId = buildingIds[0];
    // Fetch blocks for the building
    buildingsAPI.getById(buildingId)
      .then(res => setBlocks(res.data))
      .catch(() => setBlocks([]));
  }, [buildingIds]); */

  // Handlers for Users
  const handleAddUser = () => { setEditingUser(null); setUserDialogOpen(true); };
  const handleEditUser = (user) => { setEditingUser(user); setUserDialogOpen(true); };
  const handleDisableUser = (userId) => {
    usersAPI.update(userId, { isActive: false }).then(() => {
      setUsers(users => users.map(u => u.userId === userId ? { ...u, isActive: false } : u));
    });
  };
  const handleActivateUser = (userId) => {
   usersAPI.update(userId, { isActive: true }).then(() => {
    setUsers(users => users.map(u => u.userId === userId ? { ...u, isActive: true } : u));
   });
 };
 
  // Toggle user status function for mobile cards
  const handleToggleUserStatus = (userId, currentStatus) => {
    const isCurrentlyBlocked = currentStatus === 'BLOCKED';
    const newStatus = isCurrentlyBlocked ? 'ACTIVE' : 'BLOCKED';
    const newIsActive = !isCurrentlyBlocked;
    
    usersAPI.update(userId, { isActive: newIsActive, status: newStatus }).then(() => {
      setUsers(users => users.map(u => u.userId === userId ? { ...u, isActive: newIsActive, status: newStatus } : u));
    }).catch(err => {
      console.error('Error updating user status:', err);
      setUsersError('Failed to update user status');
    });
  };  

  // Handlers for Flats
  const handleAddFlat = () => { setEditingFlat(null); setFlatDialogOpen(true); };
  const handleEditFlat = async (flat) => { 
    console.log('handleEditFlat called with:', flat); // Debug log
    console.log('Flat vehicles data:', flat.vehicles); // Debug log
    
    try {
      // Fetch complete flat details including vehicle information
      const flatDetailsRes = await flatsAPI.getDetails(flat.flatId, flat.flatNumber);
      const flatDetails = flatDetailsRes.data;
      console.log('Complete flat details with vehicles:', flatDetails);
      
      setEditingFlat(flatDetails);
    } catch (error) {
      console.error('Error fetching flat details for edit:', error);
      // Fallback to original flat data if API call fails
      setEditingFlat(flat);
    }
    
    setFlatDialogOpen(true); 
  };
  const handleDeleteFlat = async (flatId) => {
    try {
      await flatsAPI.delete(flatId);
      // Refresh flats list after deletion
      const res = await flatsAPI.getByBuilding(buildingIds[0]);
      setFlats(res.data);
    } catch (err) {
      setFlatsError('Failed to delete flat');
    }
  };

  // Handlers for Complaints
  const handleAddComplaint = () => { setEditingComplaint(null); setComplaintDialogOpen(true); };
  const handleEditComplaint = (complaint) => { 
    console.log('Editing complaint:', complaint);
    console.log('Complaint category:', complaint.category);
    setEditingComplaint(complaint); 
    setComplaintDialogOpen(true); 
  };

  // Show loading spinner while fetching associations or if no building IDs
  if (isLoadingAssociations || buildingIds.length === 0) {
    return <LoadingSpinner />;
  }

  const getCurrentMonthPayments = (flatId) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    return (payments || []).find(
      p => p.flatId === flatId && 
           Number(p.year) === year && 
           Number(p.month) === month
    );
  };

  const handlePaymentMethodChange = (paymentId, value) => {
    setPaymentMethodSelections(prev => ({
      ...prev,
      [paymentId]: value
    }));
  };
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById('razorpay-script')) return resolve(true);
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRazorpayPayment = async (flat, amount, paymentMethod, year, month) => {
    try {
      const res = await loadRazorpayScript();
      if (!res) {
        console.error('Razorpay SDK failed to load');
        return;
      }

      // Create payment order on backend
      const paymentType = (year === null && month === null) ? "OutstandingBalance" : "Maintenance";
      const description = (year === null && month === null) 
        ? "Outstanding balance payment" 
        : "Monthly maintenance payment";
        
      const orderRes = await paymentsAPI.create({
        amount,
        currency: 'INR',
        flatId: flat.flatId,
        buildingId: flat.buildingId,
        userId: user.userId,
        paymentType: paymentType,
        paymentMethod: "RAZOR PAY",
        description: description,
        year: year,
        month: month
      });
      const order = orderRes.data;

      // Validate that building has Razorpay credentials configured
      if (!order.key_id) {
        throw new Error('Razorpay payment is not configured for this building. Please contact administrator to set up payment gateway.');
      }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Apartment Maintenance',
        description: 'Maintenance Payment',
        order_id: order.id,
        handler: async function (response) {
          try {
            // On successful payment, update payment status in backend
            await paymentsAPI.update(order.paymentId, {
              status: 'COMPLETED',
              flatId: flat.flatId,
              paymentId: order.paymentId,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              paymentMethod: "RAZOR PAY",
              year: year,
              month: month
            });
            
            // Force statistics refresh
            setPaymentsRefreshTrigger(t => t + 1);
            
            // Refresh flats data
            await refreshFlatsData();
            
            // Set success flag for outstanding payments
            if (year === null && month === null) {
              // This indicates it was an outstanding balance payment
              console.log('Outstanding balance payment successful!');
            } else {
              console.log('Monthly maintenance payment successful!');
            }
          } catch (error) {
            console.error('Payment update error:', error);
          }
        },
        prefill: {
          name: (user.firstName || '') + ' ' + (user.lastName || ''),
          email: user.email || '',
          contact: user.phoneNumber || ''
        },
        theme: { color: "#528FF0" },
        modal: {
          ondismiss: function() {
            console.log('Razorpay payment cancelled');
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Razorpay payment error:', error);
    }
  };

  // Handle Razorpay payment for outstanding balance (for Admin Dashboard)
  const handleAdminRazorpayPayment = async (flat, amount, paymentMethod) => {
    try {
      setPayingOutstanding(true);
      setOutstandingError('');

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setOutstandingError('Razorpay SDK failed to load. Please try again.');
        setPayingOutstanding(false);
        return;
      }

      // Create Razorpay order
      const orderRes = await paymentsAPI.createRazorpayOrder({
        amount: Number(amount), // Convert to paise
        currency: 'INR',
        flatId: flat.flatId,
        buildingId: flat.buildingId || flat.buildingID,
        paymentType: 'OutstandingBalance',
        paymentMethod: "RAZOR PAY",
        description: 'Outstanding balance payment',
        status: 'PENDING'
      });

      const order = orderRes.data;
      console.log('Razorpay order created:', order);
      //console.log('Razorpay order created internal order:', order?.order);
      // Validate that building has Razorpay credentials configured
      if (!order?.order.key_id) {
        throw new Error('Razorpay payment is not configured for this building. Please contact administrator to set up payment gateway.');
      }
      const options = {
        key: order.order.key_id,
        amount: order.order.amount,
        currency: order.order.currency,
        name: 'Apartment Maintenance',
        description: 'Outstanding Balance Payment',
        order_id: order.order.id,
        handler: async function (response) {
          try {
            // Update payment with Razorpay response
            /*await paymentsAPI.payWithRazorpay({
              flatId: flat.flatId,
              buildingId: flat.buildingId || flat.buildingID,
              paymentId: order.paymentId,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              paymentMethod: 'Online',
              paymentType: 'OutstandingBalance',
              amount: Number(amount),
              year: new Date().getFullYear(),
              month: new Date().getMonth() + 1
            });*/
            await paymentsAPI.update(order.paymentId, {
                        paymentId: order.paymentId,
                        flatId: flat.flatId,
                        status: 'COMPLETED',
                        amount: amount,
                        description: 'Payment completed via Razorpay',
                        razorpayPaymentId: response.razorpay_payment_id,
                        razorpayOrderId: response.razorpay_order_id,
                        razorpaySignature: response.razorpay_signature,
                        buildingId: flat.buildingId || flat.buildingID,
                        paymentMethod : 'RAZOR PAY',
                      });

            setOutstandingSuccess(true);
            setOutstandingError('');
            
            // Refresh flats data
            await refreshFlatsData();
            
            // Force refresh payments data
            setPaymentsRefreshTrigger(t => t + 1);
            
            console.log('Outstanding balance payment successful!');
          } catch (error) {
            console.error('Payment completion error:', error);
            setOutstandingError('Payment was successful but failed to update records. Please contact support.');
          }
        },
        prefill: {
          name: flat.ownerName || 'Apartment Owner',
          email: flat.ownerEmail || '',
          contact: flat.ownerPhone || ''
        },
        theme: { color: "#528FF0" },
        modal: {
          ondismiss: function() {
            console.log('Razorpay payment cancelled');
            setPayingOutstanding(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Razorpay payment error:', error);
      setOutstandingError('Failed to initiate payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setPayingOutstanding(false);
    }
  };

  // Toggle CCAvenue card display for a specific flat
  const toggleCCAvCard = (flatId) => {
    setShowCCAvCardMap(prev => ({
      ...prev,
      [flatId]: !prev[flatId]
    }));
  };

  // Building statistics are now provided by the summary API
  // No need for expensive calculations here

  // Filter and search flats
  const getFilteredFlats = () => {
    let filteredFlats = flats || [];
    
    // Apply payment filter
    if (flatFilter === 'pending') {
      filteredFlats = filteredFlats.filter(flat => Number(flat.outstandingBalance) > 0);
    } else if (flatFilter === 'completed') {
      filteredFlats = filteredFlats.filter(flat => Number(flat.outstandingBalance) === 0);
    }
    
    // Apply search filter
    if (flatSearchTerm.trim()) {
      const searchLower = flatSearchTerm.toLowerCase().trim();
      filteredFlats = filteredFlats.filter(flat => {
        // Basic flat information
        const basicMatch = 
          flat.flatNumber?.toLowerCase().includes(searchLower) ||
          flat.blockName?.toLowerCase().includes(searchLower) ||
          flat.flatType?.toLowerCase().includes(searchLower) ||
          `${flat.flatNumber} ${flat.blockName}`.toLowerCase().includes(searchLower);
        
        // Search through vehicles array
        const vehicleMatch = flat.vehicles?.some(vehicle =>
          vehicle.vehicleNumber?.toLowerCase().includes(searchLower) ||
          vehicle.vehicleType?.toLowerCase().includes(searchLower) ||
          vehicle.vehicleBrand?.toLowerCase().includes(searchLower) ||
          vehicle.vehicleModel?.toLowerCase().includes(searchLower) ||
          vehicle.fastTagNumber?.toLowerCase().includes(searchLower)
        );
        
        // Legacy vehicle search for backward compatibility
        const legacyVehicleMatch = 
          flat.vehicleNumber?.toLowerCase().includes(searchLower) ||
          flat.vehicleType?.toLowerCase().includes(searchLower);
        
        return basicMatch || vehicleMatch || legacyVehicleMatch;
      });
    }
    
    return filteredFlats;
  };

  // Get paginated flats
  const getPaginatedFlats = () => {
    const filteredFlats = getFilteredFlats();
    const startIndex = (currentPage - 1) * flatsPageSize;
    const endIndex = startIndex + flatsPageSize;
    return filteredFlats.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const totalFlats = getFilteredFlats().length;
  const totalPages = Math.ceil(totalFlats / flatsPageSize);

  // Compute filtered users and pages so pagination can be shown only when > 1 page
  const getFilteredUsers = () => {
    return (users || []).filter(user => {
      if (!usersSearchTerm) return true;
      const searchLower = usersSearchTerm.toLowerCase();
      const fullName = (user.fullName || `${user.firstName || ''} ${user.lastName || ''}`).toLowerCase();
      if (fullName.includes(searchLower)) return true;
      if (user.email && user.email.toLowerCase().includes(searchLower)) return true;
      if (user.userType && user.userType.toLowerCase().includes(searchLower)) return true;

      let userFlatIds = [];
      if (user.flatIds && Array.isArray(user.flatIds)) {
        userFlatIds = user.flatIds;
      } else if (user.flatId) {
        userFlatIds = [user.flatId];
      } else if (user.flats && Array.isArray(user.flats)) {
        userFlatIds = user.flats.map(flat => flat.flatId || flat);
      }

      const userFlatNumbers = userFlatIds
        .map(flatId => {
          const flatMatch = flats.find(f => f.flatId === flatId);
          return flatMatch ? flatMatch.flatNumber : flatId;
        })
        .filter(fn => fn);

      if (userFlatNumbers.some(flatNum => flatNum.toString().toLowerCase().includes(searchLower))) return true;
      return false;
    });
  };

  const usersTotalPages = Math.ceil(getFilteredUsers().length / usersPageSize);

  // Reset to first page when search or filter changes
  const handleSearchChange = (event) => {
    setFlatSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (event) => {
    setFlatFilter(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Process payment function to handle actual payment logic
  const processPayment = async (flatArg, amountArg) => {
    const flat = flatArg || selectedFlatForPayment;
    const amount = Number(amountArg ?? paymentAmount);
    if (!flat) {
      setPaymentError('No flat selected for payment.');
      return;
    }
    const outstandingAmount = Number(flat?.outstandingBalance || 0);
    
    // mark local flag safely if flat object exists
    try {
      flat._payingOutstanding = true;
    } catch (err) {
      // ignore if cannot set
    }
    
    try {
      let description;
      if (amount === outstandingAmount) {
        description = "Outstanding balance paid in full";
      } else {
        description = `Partial payment of ₹${amount} towards outstanding balance`;
      }

      // Handle cash payments directly
      await paymentsAPI.create({
        flatId: flat.flatId,
        buildingId: flat.buildingId,
        userId: user.userId,
        amount: amount,
        paymentType: "OutstandingBalance",
        paymentMethod: 'Cash',
        status: "COMPLETED",
        dueDate: new Date().toISOString(),
        description: description,
        year: null,
        month: null
      });
      
      // Wait a moment for DynamoDB to propagate the changes
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh flats data to get the actual updated outstanding balance from backend
      await refreshFlatsData();
      
      // Refresh payments to show new record
      setPaymentsRefreshTrigger(t => t + 1);
      
      // Reset form
      setSelectedFlatForPayment(null);
      setPaymentAmount('');
      setPaymentMethod('Cash');
      setPaymentError('');
      
      console.log('Cash payment recorded successfully!');
      
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('Failed to process payment. Please try again.');
    } finally {
      try {
        if (flat) flat._payingOutstanding = false;
      } catch (err) {
        // ignore
      }
    }
  };

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" color="error" gutterBottom>Not Authorized</Typography>
        <Typography>You do not have access to view the Building Admin Dashboard.</Typography>
      </Box>
    );
  }

  return (
    <Box className="mobile-content-spacing" sx={{ 
      maxWidth: 1200, 
      mx: 'auto', 
      mt: { xs: 0, sm: 0 },
      px: { xs: 1, sm: 2 }
    }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '20px', sm: '24px', md: '28px' },
          mb: { xs: 0.5, sm: 1 },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        {buildingName ? `${buildingName} - Admin Dashboard` : 'Building Admin Dashboard'}
      </Typography>
      {/* Stats Section */}
      <Box sx={{ mb: 2 }}>
        {summaryError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {summaryError}
          </Alert>
        )}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: 'repeat(2, 1fr)', 
            sm: 'repeat(3, 1fr)', 
            md: 'repeat(auto-fill, minmax(200px, 1fr))' 
          }, 
          gap: { xs: 0.5, sm: 1 }
        }}>
          <StatCard
            title={t('buildingAdmin.statistics.totalUsers')}
            value={summaryLoading ? '...' : (users || []).length}
            icon={<People fontSize={isMobile ? "medium" : "large"} />}
            color="#3f51b5"
          />
          <StatCard
            title={t('buildingAdmin.statistics.totalFlats')}
            value={summaryLoading ? '...' : (buildingSummary.totalFlats || (flats || []).length)}
            icon={<Home fontSize={isMobile ? "medium" : "large"} />}
            color="#4caf50"
          />
          <StatCard
            title={t('buildingAdmin.statistics.flatsWithPendingPayments')}
            value={summaryLoading ? '...' : (buildingSummary.flatsWithPendingPayments || flatsWithPendingPayments.length)}
            icon={<Home fontSize={isMobile ? "medium" : "large"} />}
            color="#e91e63"
          />
          <StatCard
            title={t('buildingAdmin.statistics.pendingComplaints')}
            value={summaryLoading ? '...' : (complaints || []).length}
            icon={<Report fontSize={isMobile ? "medium" : "large"} />}
            color="#f44336"
          />
          <StatCard
            title={t('buildingAdmin.statistics.monthlyRevenue')}
            value={summaryLoading ? '...' : `₹${(buildingSummary.monthlyRevenue || 0).toFixed(2)}`}
            icon={<Payment fontSize={isMobile ? "medium" : "large"} />}
            color="#ff9800"
          />
          <StatCard
            title={t('buildingAdmin.statistics.monthlyReceivables')}
            value={summaryLoading ? '...' : `₹${(buildingSummary.monthlyReceivables || 0).toFixed(2)}`}
            icon={<Payment fontSize={isMobile ? "medium" : "large"} />}
            color="#4caf50"
          />
          <StatCard
            title={t('buildingAdmin.statistics.totalOutstanding')}
            value={summaryLoading ? '...' : `₹${(buildingSummary.totalOutstanding || 0).toFixed(2)}`}
            icon={<Payment fontSize={isMobile ? "medium" : "large"} />}
            color="#ff5722"
          />
        </Box>
      </Box>
      <Tabs 
        value={tab} 
        onChange={(_, v) => setTab(v)}
        variant={isMobile ? "scrollable" : "fullWidth"}
        scrollButtons={isMobile ? "auto" : false}
        allowScrollButtonsMobile={true}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: { xs: 1, sm: 0 },
          '& .MuiTab-root': {
            fontSize: { xs: '11px', sm: '14px' },
            minWidth: { xs: 70, sm: 120 },
            padding: { xs: '8px 4px', sm: '12px 16px' },
            textTransform: 'none'
          },
          '& .MuiTabs-scrollButtons': {
            color: 'primary.main'
          },
          '& .MuiTabs-indicator': {
            height: { xs: 2, sm: 3 }
          }
        }}
      >
        <Tab label={t('buildingAdmin.tabs.users')} />
        <Tab label={t('buildingAdmin.tabs.flats')} />
        <Tab label={t('buildingAdmin.tabs.complaints')} />
        <Tab label={t('buildingAdmin.tabs.paymentsReport')} />
      </Tabs>
      {/* Users Tab */}
      <TabPanel value={tab} index={0}>
        {usersError && <Alert severity="error">{usersError}</Alert>}

        {/* Single row layout for Users controls */}
        <Box sx={{
          mb: 2,
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: { xs: 'nowrap', sm: 'wrap' }
        }}>
          <TextField
            label={t('common.search')}
            placeholder="Search by name, email, flat, or user type..."
            value={usersSearchTerm}
            size={isMobile ? "small" : "medium"}
            onChange={(e) => {
              setUsersSearchTerm(e.target.value);
              setUsersPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 280 },
              fontSize: { xs: '14px', sm: '16px' },
              flex: { xs: '1', sm: '1 1 280px' }
            }}
          />

            <FormControl 
              size={isMobile ? "small" : "medium"}
              sx={{ 
                width: { xs: '100%', sm: 120 },
                flex: { xs: '1', sm: '0 0 120px' }
              }}
            >
              <InputLabel>{t('common.pageSize')}</InputLabel>
              <Select
                value={usersPageSize}
                label={t('common.pageSize')}
                onChange={e => { setUsersPageSize(e.target.value); setUsersPage(1); }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddUser}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                fontSize: { xs: '12px', sm: '14px' },
                px: { xs: 2, sm: 3 },
                flex: { xs: '1', sm: '0 0 auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              {t('buildingAdmin.users.addUser')}
            </Button>
        </Box>
        
        {/* Results counter moved to bottom (below pagination) */}
        
        {isMobile ? (
          // Mobile Card Layout
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(() => {
              // Filter users based on search term
              const filteredUsers = (users || []).filter(user => {
                if (!usersSearchTerm) return true;
                const searchLower = usersSearchTerm.toLowerCase();
                
                // Search in name (firstName + lastName or fullName)
                const fullName = (user.fullName || `${user.firstName || ''} ${user.lastName || ''}`).toLowerCase();
                if (fullName.includes(searchLower)) return true;
                
                // Search in email
                if (user.email && user.email.toLowerCase().includes(searchLower)) return true;
                
                // Search in user type
                if (user.userType && user.userType.toLowerCase().includes(searchLower)) return true;
                
                // Search in flat numbers
                let userFlatIds = [];
                if (user.flatIds && Array.isArray(user.flatIds)) {
                  userFlatIds = user.flatIds;
                } else if (user.flatId) {
                  userFlatIds = [user.flatId];
                } else if (user.flats && Array.isArray(user.flats)) {
                  userFlatIds = user.flats.map(flat => flat.flatId || flat);
                }
                
                const userFlatNumbers = userFlatIds
                  .map(flatId => {
                    const flatMatch = flats.find(f => f.flatId === flatId);
                    return flatMatch ? flatMatch.flatNumber : flatId;
                  })
                  .filter(fn => fn);
                
                if (userFlatNumbers.some(flatNum => flatNum.toString().toLowerCase().includes(searchLower))) return true;
                
                return false;
              });
              
              // Apply pagination
              const startIndex = (usersPage - 1) * usersPageSize;
              const endIndex = startIndex + usersPageSize;
              const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
              
              return paginatedUsers.map(user => {
                // Get flat numbers for this user
                let userFlatNumbers = [];
                
                // Handle multiple flat ID formats from API response
                let userFlatIds = [];
                if (user.flatIds && Array.isArray(user.flatIds)) {
                  userFlatIds = user.flatIds;
                } else if (user.flatId) {
                  userFlatIds = [user.flatId];
                } else if (user.flats && Array.isArray(user.flats)) {
                  userFlatIds = user.flats.map(flat => flat.flatId || flat);
                }
                
                userFlatNumbers = userFlatIds
                  .map(flatId => {
                    const flatMatch = flats.find(f => f.flatId === flatId);
                    return flatMatch ? flatMatch.flatNumber : flatId;
                  })
                  .filter(fn => fn);

                return (
                  <Card key={user.userId} variant="outlined" sx={{ p: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                          {user.fullName || user.name || 'N/A'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip 
                            label={user.userType || 'N/A'} 
                            size="small" 
                          sx={{ fontSize: '10px', height: '20px' }}
                        />
                        <Chip 
                          label={user.status || 'ACTIVE'} 
                          color={user.status === 'BLOCKED' ? 'error' : 'success'} 
                          size="small" 
                          sx={{ fontSize: '10px', height: '20px' }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                      Email: {user.email || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
                      Flats: {userFlatNumbers.length > 0 ? userFlatNumbers.join(', ') : 'None'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEditUser(user)}
                        sx={{ fontSize: '11px' }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color={user.status === 'BLOCKED' ? 'success' : 'error'}
                        size="small"
                        startIcon={<Block />}
                        onClick={() => handleToggleUserStatus(user.userId, user.status)}
                        sx={{ fontSize: '11px' }}
                      >
                        {user.status === 'BLOCKED' ? 'Unblock' : 'Block'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
                );
              });
            })()}
          </Box>
        ) : (
          // Desktop Table Layout
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('buildingAdmin.users.name')}</TableCell>
                  <TableCell>{t('buildingAdmin.users.email')}</TableCell>
                  <TableCell>{t('buildingAdmin.users.userType')}</TableCell>
                  <TableCell>{t('buildingAdmin.users.flats')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Filter users based on search term
                  const filteredUsers = (users || []).filter(user => {
                    if (!usersSearchTerm) return true;
                    const searchLower = usersSearchTerm.toLowerCase();
                    
                    // Search in name (firstName + lastName or fullName)
                    const fullName = (user.fullName || `${user.firstName || ''} ${user.lastName || ''}`).toLowerCase();
                    if (fullName.includes(searchLower)) return true;
                    
                    // Search in email
                    if (user.email && user.email.toLowerCase().includes(searchLower)) return true;
                    
                    // Search in user type
                    if (user.userType && user.userType.toLowerCase().includes(searchLower)) return true;
                    
                    // Search in flat numbers
                    let userFlatIds = [];
                    if (user.flatIds && Array.isArray(user.flatIds)) {
                      userFlatIds = user.flatIds;
                    } else if (user.flatId) {
                      userFlatIds = [user.flatId];
                    } else if (user.flats && Array.isArray(user.flats)) {
                      userFlatIds = user.flats.map(flat => flat.flatId || flat);
                    }
                    
                    const userFlatNumbers = userFlatIds
                      .map(flatId => {
                        const flatMatch = flats.find(f => f.flatId === flatId);
                        return flatMatch ? flatMatch.flatNumber : flatId;
                      })
                      .filter(fn => fn);
                    
                    if (userFlatNumbers.some(flatNum => flatNum.toString().toLowerCase().includes(searchLower))) return true;
                    
                    return false;
                  });
                  
                  // Apply pagination
                  const startIndex = (usersPage - 1) * usersPageSize;
                  const endIndex = startIndex + usersPageSize;
                  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
                  
                  return paginatedUsers.map(user => {
                  // Get flat numbers for this user
                  let userFlatNumbers = [];
                  
                  // Handle multiple flat ID formats from API response
                  let userFlatIds = [];
                  if (user.flatIds && Array.isArray(user.flatIds)) {
                    userFlatIds = user.flatIds;
                  } else if (user.flatId) {
                    userFlatIds = [user.flatId];
                  } else if (user.flats && Array.isArray(user.flats)) {
                    userFlatIds = user.flats.map(flat => flat.flatId || flat);
                  }
              
                  
                  // Map flat IDs to flat numbers
                  userFlatNumbers = userFlatIds.map(flatId => {
                    const flat = (flats || []).find(f => f.flatId === flatId);
                    return flat ? `${flat.flatNumber}${flat.blockName ? ` (${flat.blockName})` : ''}` : flatId;
                  }).filter(Boolean);
                  
                  return (
                    <TableRow key={user.userId}>
                      <TableCell>{user.firstName} {user.lastName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.userType}</TableCell>
                      <TableCell>
                        {userFlatNumbers.length > 0 ? userFlatNumbers.join(', ') : 'No flats assigned'}
                      </TableCell>
                      <TableCell>{user.isActive ? 'Active' : 'Disabled'}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditUser(user)}>
                          <Edit />
                        </IconButton>
                        {user.isActive ? (
                          <IconButton
                            onClick={() => handleDisableUser(user.userId)}
                            color="error"
                            title="Deactivate User"
                          >
                            <Block />
                          </IconButton>
                        ) : (
                          <IconButton
                            onClick={() => handleActivateUser(user.userId)}
                            color="success"
                            title="Activate User"
                          >
                            <Block />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Pagination and results counter (parallel on desktop, stacked on mobile) - show only when >1 page */}
        {usersTotalPages > 1 && (
          <Box sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2
          }}>
            <Pagination
              count={usersTotalPages}
              page={usersPage}
              onChange={(event, value) => setUsersPage(value)}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: isMobile ? 1 : 0 }}>
              {t('common.showingResults', {
                start: Math.min((usersPage - 1) * usersPageSize + 1, getFilteredUsers().length),
                end: Math.min(usersPage * usersPageSize, getFilteredUsers().length),
                total: getFilteredUsers().length
              })}
            </Typography>
          </Box>
        )}

        <UserDialog
          open={userDialogOpen}
          onClose={() => setUserDialogOpen(false)}
          onSubmit={async (data) => {
            try {
              // Debug logging for flat associations
              console.log('User form data being submitted:', data);
              console.log('Flat IDs being associated:', data.flatIds);
              
              if (editingUser) {
                console.log('Updating user:', editingUser.userId, 'with data:', data);
                await usersAPI.update(data.userId, data); // Update user
              } else {
                console.log('Creating new user with data:', data);
                await usersAPI.create(data); // Create user
              }
              
              // Refresh users list after update or creation
              const res = await usersAPI.getUsers(buildingIds[0]);
              console.log('Refreshed users data:', res.data);
              setUsers(res.data);
              setUserDialogOpen(false);
            } catch (err) {
              console.error('Error in user operation:', err);
              setUsersError(editingUser ? t('buildingAdmin.users.updateFailed') : t('buildingAdmin.users.addFailed'));
            }
          }}
          flats={flats}
          buildingIds={buildingIds}
          editingUser={editingUser}
          isLoading={false}
          error={usersError}
        />
      </TabPanel>      {/* Flats Tab */}
      <TabPanel value={tab} index={1}>
        {flatsError && <Alert severity="error">{flatsError}</Alert>}
        
        {/* Single row layout for Flats controls */}
        <Box sx={{ 
          mb: 2,
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: { xs: 'nowrap', sm: 'wrap' }
        }}>
          <TextField
            placeholder="Search flats by number, block, type, or vehicle..."
            value={flatSearchTerm}
            onChange={handleSearchChange}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              width: { xs: '100%', sm: 280 },
              fontSize: { xs: '14px', sm: '16px' },
              flex: { xs: '1', sm: '1 1 280px' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl 
            variant="outlined" 
            size={isMobile ? "small" : "medium"} 
            sx={{ 
              width: { xs: '100%', sm: 180 },
              flex: { xs: '1', sm: '0 0 180px' }
            }}
          >
            <InputLabel>{t('buildingAdmin.flats.filterFlats')}</InputLabel>
            <Select
              value={flatFilter}
              onChange={handleFilterChange}
              label="Filter Flats"
            >
              <MenuItem value="all">{t('buildingAdmin.flats.allFlats')}</MenuItem>
              <MenuItem value="pending">{t('buildingAdmin.flats.pendingPayments')}</MenuItem>
              <MenuItem value="completed">{t('buildingAdmin.flats.completedPayments')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl 
            size={isMobile ? "small" : "medium"}
            sx={{ 
              width: { xs: '100%', sm: 120 },
              flex: { xs: '1', sm: '0 0 120px' }
            }}
          >
            <InputLabel>{t('common.pageSize')}</InputLabel>
            <Select
              value={flatsPageSize}
              label={t('common.pageSize')}
              onChange={e => { setFlatsPageSize(e.target.value); setCurrentPage(1); }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={handleAddFlat}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              fontSize: { xs: '12px', sm: '14px' },
              px: { xs: 2, sm: 3 },
              flex: { xs: '1', sm: '0 0 auto' },
              minWidth: { xs: 'auto', sm: 120 }
            }}
          >
            {t('buildingAdmin.flats.addFlat')}
          </Button>
        </Box>

        {/* Removed top results summary and pagination — using bottom controls for consistency */}
        
        <Table>
          <TableHead>
            {/* Removed Flat Number, Type, Area, Floor, Actions header row as requested */}
          </TableHead>
          <TableBody>
            {getPaginatedFlats().length === 0 ? (
              <TableRow>
                <TableCell colSpan={1} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('buildingAdmin.flats.noFlatsFound')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {flatSearchTerm ? 
                      t('buildingAdmin.flats.noFlatsMatch', { searchTerm: flatSearchTerm }) : 
                      flatFilter !== 'all' ? 
                        t('buildingAdmin.flats.noFlatsWithFilter', { filter: flatFilter }) :
                        t('buildingAdmin.flats.noFlatsAvailable')
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              getPaginatedFlats().map(flat => {
  return (
    <Accordion
      key={flat.flatId}
      sx={{ mb: 2 }}
      expanded={expandedFlatId === flat.flatId}
      onChange={handleAccordionChange(flat.flatId)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">
              {t('buildingAdmin.flats.flatDisplay', { 
                flatNumber: flat.flatNumber, 
                blockName: flat.blockName ? `(${flat.blockName})` : '' 
              })}
            </Typography>            
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditFlat(flat);
              }}
              title="Edit Flat"
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Are you sure you want to delete Flat ${flat.flatNumber}?`)) {
                  handleDeleteFlat(flat.flatId);
                }
              }}
              title="Delete Flat"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <div>
          {flatLoadingMap[flat.flatId] ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <LoadingSpinner />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading flat details...
              </Typography>
            </Box>
          ) : null}
          {!flatLoadingMap[flat.flatId] && (
            <>
              {/* Payment Gateway Buttons - Razorpay and CCAvenue */}
              {Number(flat.outstandingBalance) > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
                    Outstanding Balance (incl. current month): ₹{flat.outstandingBalance}
                  </Alert>
                  
                  {/* Payment amount and method selection - hide when CCAvenue card is shown */}
                  {!showCCAvCardMap[flat.flatId] && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: { xs: 1, sm: 2 }, 
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    width: '100%',
                    mb: 2
                  }}>
                    <Typography variant="body2" sx={{ 
                      mr: { xs: 0, sm: 1 }, 
                      fontSize: { xs: '14px', sm: '16px' },
                      mb: { xs: 1, sm: 0 },
                      alignSelf: 'flex-start',
                      mt: { xs: 0, sm: 1 }
                    }}>
                      Payment Amount:
                    </Typography>
                    
                    <TextField
                      size="small"
                      type="number"
                      value={selectedFlatForPayment?.flatId === flat.flatId ? paymentAmount : flat.outstandingBalance.toString()}
                      onChange={(e) => {
                        setSelectedFlatForPayment(flat);
                        setPaymentAmount(e.target.value);
                        setPaymentError('');
                      }}
                      inputProps={{ 
                        min: 0, 
                        max: flat.outstandingBalance,
                        step: 0.01
                      }}
                      sx={{ 
                        width: { xs: '100%', sm: '150px' },
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '14px', sm: '16px' }
                        }
                      }}
                    />
                    
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={(selectedFlatForPayment?.flatId === flat.flatId ? paymentMethod : 'Cash') === 'Cash'}
                            onChange={() => {
                              setSelectedFlatForPayment(flat);
                              if (!paymentAmount) {
                                setPaymentAmount(flat.outstandingBalance.toString());
                              }
                              setPaymentMethod('Cash');
                              setPaymentError('');
                            }}
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Money sx={{ fontSize: '18px', color: '#4caf50' }} />
                            <Typography sx={{ fontSize: { xs: '12px', sm: '14px' } }}>Cash</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={(selectedFlatForPayment?.flatId === flat.flatId ? paymentMethod : 'Cash') === 'Razorpay'}
                            onChange={() => {
                              setSelectedFlatForPayment(flat);
                              if (!paymentAmount) {
                                setPaymentAmount(flat.outstandingBalance.toString());
                              }
                              setPaymentMethod('Razorpay');
                              setPaymentError('');
                            }}
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CreditCard sx={{ fontSize: '18px', color: '#1976d2' }} />
                            <Typography sx={{ fontSize: { xs: '12px', sm: '14px' } }}>Razorpay</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={(selectedFlatForPayment?.flatId === flat.flatId ? paymentMethod : 'Cash') === 'CCAvenue'}
                            onChange={() => {
                              setSelectedFlatForPayment(flat);
                              if (!paymentAmount) {
                                setPaymentAmount(flat.outstandingBalance.toString());
                              }
                              setPaymentMethod('CCAvenue');
                              setPaymentError('');
                            }}
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccountBalance sx={{ fontSize: '18px', color: '#ff9800' }} />
                            <Typography sx={{ fontSize: { xs: '12px', sm: '14px' } }}>CCAvenue</Typography>
                          </Box>
                        }
                      />
                    </Box>
                    
                    {!showCCAvCardMap[flat.flatId] && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        // Set the selected flat if not already set
                        if (!selectedFlatForPayment || selectedFlatForPayment.flatId !== flat.flatId) {
                          setSelectedFlatForPayment(flat);
                          setPaymentAmount(flat.outstandingBalance.toString());
                          setPaymentMethod('Cash');
                        }
                        
                        const currentAmount = selectedFlatForPayment?.flatId === flat.flatId ? paymentAmount : flat.outstandingBalance.toString();
                        const currentMethod = selectedFlatForPayment?.flatId === flat.flatId ? paymentMethod : 'Cash';
                        
                        setPaymentError('');
                        
                        if (!currentAmount || isNaN(currentAmount)) {
                          setPaymentError('Please enter a valid payment amount.');
                          return;
                        }
                        
                        const amount = Number(currentAmount);
                        const outstandingAmount = Number(flat.outstandingBalance || 0);
                        
                        if (amount <= 0) {
                          setPaymentError('Payment amount must be greater than 0.');
                          return;
                        }
                        
                        if (amount > outstandingAmount) {
                          setPaymentError(`Payment amount cannot exceed outstanding balance of ₹${outstandingAmount}.`);
                          return;
                        }
                        
                        // Handle payment scenarios for all methods
                        if (amount === outstandingAmount) {
                          // Scenario A: Exact outstanding amount - proceed directly
                          if (currentMethod === 'Razorpay') {
                            handleAdminRazorpayPayment(flat, amount, 'Online');
                          } else if (currentMethod === 'CCAvenue') {
                            setCcavenuePaymentAmount(prev => ({
                              ...prev,
                              [flat.flatId]: amount
                            }));
                            toggleCCAvCard(flat.flatId);
                          } else {
                            processPayment(flat, amount);
                          }
                        } else {
                          // Scenario B: Less than outstanding - show confirmation
                          const remainingBalance = outstandingAmount - amount;
                          setPaymentConfirmationType('partial');
                          setPaymentConfirmationMessage(
                            `You are paying ₹${amount} against an outstanding balance of ₹${outstandingAmount}. ` +
                            `The remaining balance will be ₹${remainingBalance}. Do you want to continue?`
                          );
                          // Store current method and amount for confirmation callback
                          setSelectedFlatForPayment({...flat, _paymentMethod: currentMethod, _paymentAmount: amount});
                          setShowPaymentConfirmation(true);
                        }
                      }}
                      disabled={payingOutstanding}
                      sx={{ 
                        fontSize: { xs: '11px', sm: '13px' },
                        minWidth: { xs: '100%', sm: '100px' },
                        whiteSpace: 'nowrap',
                        px: { xs: 1, sm: 2 },
                        py: { xs: 0.5, sm: 1 }
                      }}
                    >
                      Pay Now
                    </Button>
                  )}
                  </Box>
                  )}
                  
                  {/* Show payment error if any */}
                  {selectedFlatForPayment?.flatId === flat.flatId && paymentError && (
                    <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '12px', sm: '14px' } }}>
                      {paymentError}
                    </Alert>
                  )}

                  {/* Error and Success Messages */}
                  {outstandingError && (
                    <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
                      {outstandingError}
                    </Alert>
                  )}
                  
                  {outstandingSuccess && (
                    <Alert severity="success" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
                      Payment processed successfully! Your payment will be reflected shortly.
                    </Alert>
                  )}

                  {/* CCAvenue Payment Card - Display when CCAvenue button is clicked */}
                  {showCCAvCardMap[flat.flatId] && (
                    <Box sx={{ mb: 3 }}>
                      <CCAvPaymentCard 
                        flat={flat}
                        amount={ccavenuePaymentAmount[flat.flatId] || flat.outstandingBalance}
                        paymentType="OutstandingBalance"
                        onClose={() => toggleCCAvCard(flat.flatId)}
                        onSuccess={() => {
                          // Handle payment success
                          setOutstandingSuccess(true);
                          setOutstandingError('');
                          toggleCCAvCard(flat.flatId);
                          // Clear the stored amount
                          setCcavenuePaymentAmount(prev => {
                            const updated = { ...prev };
                            delete updated[flat.flatId];
                            return updated;
                          });
                          // Refresh flats data
                          refreshFlatsData();
                          // Force refresh payments data
                          setPaymentsRefreshTrigger(t => t + 1);
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
              {/* Enhanced Flat Details Section */}
              {(() => {
                //const flatDetails = flatDetailsMap[flat.flatId];
                const displayFlat = flat ; // Use loaded details if available, fallback to basic flat data
                
                return (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 1, mb: 2 }}>
                      {t('buildingAdmin.flats.flatInformation')}
                    </Typography>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Type:</strong> {displayFlat.flatType} | <strong>Area:</strong> {displayFlat.area} sq ft | <strong>Floor:</strong> {displayFlat.floorNumber}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Monthly Maintenance:</strong> ₹{displayFlat.monthlyMaintenance}
                      </Typography>
                      <Typography variant="body2" gutterBottom color="error">
                        <strong>Outstanding Balance (incl. current month):</strong> ₹{displayFlat.outstandingBalance || 0}
                      </Typography>
                      {displayFlat.currentOwnerId && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Current Owner ID:</strong> {displayFlat.currentOwnerId}
                        </Typography>
                      )}
                      {displayFlat.currentTenantIds && displayFlat.currentTenantIds.length > 0 && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Current Tenant IDs:</strong> {displayFlat.currentTenantIds.join(', ')}
                        </Typography>
                      )}
                      {displayFlat.isOccupied !== undefined && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Occupancy Status:</strong> {displayFlat.isOccupied ? 'Occupied' : 'Vacant'}
                        </Typography>
                      )}
                      {displayFlat.depositAmount && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Security Deposit:</strong> ₹{displayFlat.depositAmount}
                        </Typography>
                      )}
                    </Box>
                  </>
                );
              })()}
              {/* Enhanced Vehicle Details Section */}
              {(() => {
                const flatDetails = flatDetailsMap[flat.flatId];
                const displayFlat = flatDetails || flat;
                const vehicles = displayFlat.vehicles;
                
                return (
                  <>
                    {vehicles && vehicles.length > 0 ? (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                          {t('buildingAdmin.flats.vehicleDetails')} ({vehicles.length} {vehicles.length > 1 ? t('buildingAdmin.flats.vehicles') : t('buildingAdmin.flats.vehicle')})
                        </Typography>
                        {vehicles.map((vehicle, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Vehicle {index + 1}
                              {vehicle.vehicleId && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                  (ID: {vehicle.vehicleId})
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              <strong>Type:</strong> {vehicle.vehicleType || 'N/A'} | 
                              <strong> Number:</strong> {vehicle.vehicleNumber || 'N/A'}
                            </Typography>
                            {vehicle.vehicleBrand && (
                              <Typography variant="body2" gutterBottom>
                                <strong>Brand:</strong> {vehicle.vehicleBrand} | <strong>Model:</strong> {vehicle.vehicleModel || 'N/A'}
                              </Typography>
                            )}
                            {vehicle.fastTagNumber && (
                              <Typography variant="body2" gutterBottom>
                                <strong>Fast Tag:</strong> {vehicle.fastTagNumber}
                              </Typography>
                            )}
                            {vehicle.isActive !== undefined && (
                              <Typography variant="body2" gutterBottom>
                                <strong>Status:</strong> {vehicle.isActive ? 'Active' : 'Inactive'}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </>
                    ) : (
                      // Legacy single vehicle display for backward compatibility
                      (displayFlat.vehicleType || displayFlat.vehicleNumber || displayFlat.fastTagNumber) && (
                        <>
                          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            {t('buildingAdmin.flats.vehicleDetailsLegacy')}
                          </Typography>
                          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" gutterBottom>
                              <strong>Vehicle Type:</strong> {displayFlat.vehicleType || 'N/A'} | 
                              <strong> Vehicle Number:</strong> {displayFlat.vehicleNumber || 'N/A'}
                            </Typography>
                            {displayFlat.vehicleBrand && (
                              <Typography variant="body2" gutterBottom>
                                <strong>Brand:</strong> {displayFlat.vehicleBrand} | <strong>Model:</strong> {displayFlat.vehicleModel || 'N/A'}
                              </Typography>
                            )}
                            {displayFlat.fastTagNumber && (
                              <Typography variant="body2" gutterBottom>
                                <strong>Fast Tag:</strong> {displayFlat.fastTagNumber}
                              </Typography>
                            )}
                          </Box>
                        </>
                      )
                    )}
                  </>
                );
              })()}
              {/* Enhanced Payment History Section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {t('buildingAdmin.payments.paymentHistory')}
              </Typography>
              {(() => {
                const flatDetails = flatDetailsMap[flat.flatId];
                
                // Use enhanced payment history from flat details if available, fallback to current payments
                let flatPayments = [];
                
                if (flatDetails && flatDetails.completedPaymentHistory && flatDetails.completedPaymentHistory.length > 0) {
                  // Use the enhanced completed payment history from flat details
                  flatPayments = flatDetails.completedPaymentHistory;
                } else {
                  // Fallback to current payments data
                  flatPayments = (payments || []).filter(p => p.flatId === flat.flatId);
                }
                
                if (flatPayments.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {flatDetailsMap[flat.flatId] ? 'No completed payment history found.' : t('buildingAdmin.payments.noPaymentHistoryFound')}
                    </Typography>
                  );
                }
                
                return (
                  <>
                    {flatDetails && flatDetails.completedPaymentHistory && (
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Showing completed payments only. Total: {flatPayments.length} payments
                      </Typography>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('buildingAdmin.payments.paymentId')}</TableCell>
                          <TableCell>{t('buildingAdmin.payments.amount')}</TableCell>
                          <TableCell>{t('buildingAdmin.payments.paymentType')}</TableCell>
                          <TableCell>{t('common.status')}</TableCell>
                          <TableCell>{t('buildingAdmin.payments.monthYear')}</TableCell>
                          <TableCell>{t('buildingAdmin.payments.dueDate')}</TableCell>
                          <TableCell>{t('buildingAdmin.payments.paidDate')}</TableCell>
                          <TableCell>{t('buildingAdmin.payments.description')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const sortedPayments = [...flatPayments]
                            .sort((a, b) => (Date.parse(b.paidDate || b.createdAt || b.dueDate) || 0) - (Date.parse(a.paidDate || a.createdAt || a.dueDate) || 0))
                            .slice(0, 6);

                          return sortedPayments.map(payment => {
                            let monthYear = '-';
                            if (payment.paymentType === 'Outstanding' || payment.year == null || payment.month == null) {
                              monthYear = 'Outstanding';
                            } else {
                              const monthName = new Date(payment.year, (payment.month || 1) - 1).toLocaleString('default', { month: 'long' });
                              monthYear = `${monthName} ${payment.year}`;
                            }

                            return (
                              <TableRow key={payment.paymentId}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {payment.paymentId}
                                  </Typography>
                                </TableCell>
                                <TableCell>₹{payment.amount}</TableCell>
                                <TableCell>{payment.paymentType}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      bgcolor: payment.status === 'COMPLETED' ? '#e8f5e8' : payment.status === 'PENDING' ? '#fff3e0' : '#ffebee',
                                      color: payment.status === 'COMPLETED' ? '#2e7d32' : payment.status === 'PENDING' ? '#f57c00' : '#d32f2f'
                                    }}
                                  >
                                    {payment.status}
                                  </Box>
                                </TableCell>
                                <TableCell>{monthYear}</TableCell>
                                <TableCell>{payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : '-'}</TableCell>
                                <TableCell>{payment.paidDate ? new Date(payment.paidDate).toLocaleDateString() : '-'}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {payment.description || '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                    {flatPayments.length > 6 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Showing latest 6 payments of {flatPayments.length} total payments
                      </Typography>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
})
            )}
            
          </TableBody>
        </Table>
        
        {/* Pagination + results counter (parallel on desktop, stacked on mobile) */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2, flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: 2 }}>
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: isMobile ? 1 : 0 }}>
              {(() => {
                const filtered = getFilteredFlats();
                const start = Math.min((currentPage - 1) * flatsPageSize + 1, filtered.length);
                const end = Math.min(currentPage * flatsPageSize, filtered.length);
                return `Showing ${start}-${end} of ${filtered.length} flats`;
              })()}
            </Typography>
          </Box>
        )}
        
        <FlatDialog
          open={flatDialogOpen}
          onClose={() => setFlatDialogOpen(false)}
          onSubmit={async (data) => {
            try {
              // If editing, update; else, create
              const flatPayload = {
                ...data,
                outstandingBalance: parseFloat(data.outstandingBalance) || 0
              };
              if (editingFlat) {
                await flatsAPI.update(data.flatId, flatPayload);
                
                // Update the flat in the flats list with the new data
                setFlats(prevFlats => 
                  prevFlats.map(flat => 
                    flat.flatId === data.flatId ? { ...flat, ...flatPayload } : flat
                  )
                );
                
                // If this flat is currently expanded, refresh its detailed data
                if (expandedFlatId === data.flatId) {
                  try {
                    const updatedFlatDetails = await flatsAPI.getDetails(data.flatId, data.flatNumber);
                    setFlatDetailsMap(prev => ({
                      ...prev,
                      [data.flatId]: updatedFlatDetails.data
                    }));
                  } catch (detailsError) {
                    console.error('Error refreshing flat details:', detailsError);
                  }
                }
              } else {
                await flatsAPI.create(flatPayload);
                // Refresh flats list after creation
                const res = await flatsAPI.getByBuilding(buildingIds[0]);
                setFlats(res.data);
              }
              setFlatDialogOpen(false);
            } catch (err) {
              setFlatsError(editingFlat ? t('buildingAdmin.flats.updateFailed') : t('buildingAdmin.flats.addFailed'));
            }
          }}
          buildingId={buildingIds[0]}
          blocks={blocks}
          //flats={flats}
          editingFlat={editingFlat}
          isLoading={false}
          error={flatsError}
        />
      </TabPanel>

      {/* Complaints Tab */}
      <TabPanel value={tab} index={2}>
        {complaintsError && <Alert severity="error">{complaintsError}</Alert>}

        {/* Single row layout for Complaints controls */}
        <Box sx={{
          mb: 2,
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: { xs: 'nowrap', sm: 'wrap' }
        }}>
              <TextField
                label={t('common.startDate')}
                type="date"
                value={complaintsStartDate}
                size={isMobile ? "small" : "medium"}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  const maxEndDate = new Date(newStartDate);
                  maxEndDate.setMonth(maxEndDate.getMonth() + 6);
                  
                  setComplaintsStartDate(newStartDate);
                  
                  // Adjust end date if it exceeds 6 months from start date
                  if (new Date(complaintsEndDate) > maxEndDate) {
                    setComplaintsEndDate(maxEndDate.toISOString().split('T')[0]);
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ 
                  width: { xs: '100%', sm: 150 },
                  flex: { xs: '1', sm: '0 0 150px' }
                }}
              />

              <TextField
                label={t('common.endDate')}
                type="date"
                value={complaintsEndDate}
                size={isMobile ? "small" : "medium"}
                onChange={(e) => {
                  const newEndDate = e.target.value;
                  const minStartDate = new Date(newEndDate);
                  minStartDate.setMonth(minStartDate.getMonth() - 6);
                  
                  setComplaintsEndDate(newEndDate);
                  
                  // Adjust start date if it's more than 6 months before end date
                  if (new Date(complaintsStartDate) < minStartDate) {
                    setComplaintsStartDate(minStartDate.toISOString().split('T')[0]);
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ 
                  width: { xs: '100%', sm: 150 },
                  flex: { xs: '1', sm: '0 0 150px' }
                }}
              />

            <FormControl 
              size={isMobile ? "small" : "medium"}
              sx={{ 
                width: { xs: '100%', sm: 120 },
                flex: { xs: '1', sm: '0 0 120px' }
              }}
            >
              <InputLabel>{t('common.pageSize')}</InputLabel>
              <Select
                value={complaintsPageSize}
                label="Page Size"
                onChange={e => {
                  setComplaintsPageSize(e.target.value);
                  setComplaintsPage(1); // Reset to first page when page size changes
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={handleAddComplaint}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                fontSize: { xs: '12px', sm: '14px' },
                px: { xs: 2, sm: 3 },
                flex: { xs: '1', sm: '0 0 auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              {t('buildingAdmin.complaints.addComplaint')}
            </Button>
        </Box>
        
        {/* Removed top complaints counter — will display with bottom pagination */}
        
        {isMobile ? (
          // Mobile Card Layout
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(() => {
              const startIndex = (complaintsPage - 1) * complaintsPageSize;
              const endIndex = startIndex + complaintsPageSize;
              const paginatedComplaints = (complaints || []).slice(startIndex, endIndex);
              
              return paginatedComplaints.map(complaint => (
                <Card key={complaint.complaintId} variant="outlined" sx={{ p: 1 }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {complaint.title || 'No Title'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip 
                          label={complaint.category || 'General'} 
                          size="small" 
                          sx={{ fontSize: '10px', height: '20px' }}
                        />
                        <Chip 
                          label={complaint.status || 'Open'} 
                          color={complaint.status === 'Resolved' ? 'success' : complaint.status === 'In Progress' ? 'warning' : 'default'} 
                          size="small" 
                          sx={{ fontSize: '10px', height: '20px' }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                      {complaint.description || 'No description'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                      Created: {complaint.createdAt ? new Date(complaint.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEditComplaint(complaint)}
                        sx={{ fontSize: '11px' }}
                      >
                        Edit
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ));
            })()}
          </Box>
        ) : (
          // Desktop Table Layout
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('buildingAdmin.complaints.title')}</TableCell>
                  <TableCell>{t('buildingAdmin.complaints.description')}</TableCell>
                  <TableCell>{t('buildingAdmin.complaints.category')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('buildingAdmin.complaints.createdAt')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const startIndex = (complaintsPage - 1) * complaintsPageSize;
                  const endIndex = startIndex + complaintsPageSize;
                  const paginatedComplaints = (complaints || []).slice(startIndex, endIndex);
                  
                  return paginatedComplaints.map(complaint => (
                    <TableRow key={complaint.complaintId}>
                      <TableCell>{complaint.title}</TableCell>
                      <TableCell>{complaint.description}</TableCell>
                      <TableCell>{complaint.category}</TableCell>
                      <TableCell>{complaint.status}</TableCell>
                      <TableCell>{complaint.createdAt ? new Date(complaint.createdAt).toLocaleString() : ''}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditComplaint(complaint)}><Edit /></IconButton>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Improved pagination for mobile - show only when >1 page */}
        {Math.ceil((complaints || []).length / complaintsPageSize) > 1 && (
          <Box sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 0 }
          }}>
            <Pagination
              count={Math.ceil((complaints || []).length / complaintsPageSize)}
              page={complaintsPage}
              onChange={(event, value) => setComplaintsPage(value)}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={isMobile ? 1 : 2}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: { xs: '12px', sm: '14px' },
                  minWidth: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 }
                }
              }}
            />

            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mt: { xs: 0.5, sm: 0 },
                fontSize: { xs: '12px', sm: '14px' },
                textAlign: 'center'
              }}
            >
              {t('common.showingResults', {
                start: Math.min((complaintsPage - 1) * complaintsPageSize + 1, (complaints || []).length),
                end: Math.min(complaintsPage * complaintsPageSize, (complaints || []).length),
                total: (complaints || []).length
              })}
            </Typography>
          </Box>
        )}
        
        <ComplaintDialog
          open={complaintDialogOpen}
          onClose={() => setComplaintDialogOpen(false)}
          onSubmit={async (data) => {
            try {
              if (editingComplaint) {
                // Edit mode: update complaint
                await complaintsAPI.update(editingComplaint.complaintId, {
                  ...editingComplaint,
                  ...data
                });
              } else {
                // Add mode: create complaint
                await complaintsAPI.create({
                  ...data
                });
              }
              // Refresh complaints list after update or creation with date filtering
              const res = await complaintsAPI.getByBuilding(buildingIds[0]);
              let complaintsData = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
              
              // Apply date filtering
              const startDate = new Date(complaintsStartDate);
              const endDate = new Date(complaintsEndDate);
              endDate.setHours(23, 59, 59, 999); // Include end of day
              
              complaintsData = complaintsData.filter(complaint => {
                if (!complaint.createdAt) return true; // Include complaints without date
                const createdDate = new Date(complaint.createdAt);
                return createdDate >= startDate && createdDate <= endDate;
              });
              
              setComplaints(complaintsData);
              setComplaintDialogOpen(false);
            } catch (err) {
              setComplaintsError(editingComplaint ? t('buildingAdmin.complaints.updateFailed') : t('buildingAdmin.complaints.addFailed'));
            }
          }}
          buildingIds={buildingIds}
          flats={flats}
          editingComplaint={editingComplaint}
          isLoading={false}
          error={complaintsError}
        />
      </TabPanel>

      {/* Payments Tab */}
      <TabPanel value={tab} index={3}>
        {paymentsError && <Alert severity="error">{paymentsError}</Alert>}
        {/* Flat search and selection */}
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Autocomplete
            sx={{ 
              minWidth: { xs: '100%', sm: 220 }
            }}
            options={[
              { flatId: '', label: t('buildingAdmin.payments.allFlats'), flatNumber: 'All', blockName: '' },
              ...(flats || []).map(flat => ({
                flatId: flat.flatId,
                label: `Flat ${flat.flatNumber} ${flat.blockName ? `(${flat.blockName})` : ''}`,
                flatNumber: flat.flatNumber,
                blockName: flat.blockName || ''
              }))
            ]}
            value={(flats || []).find(flat => flat.flatId === selectedFlatId) ? 
              { 
                flatId: selectedFlatId, 
                label: `Flat ${(flats || []).find(flat => flat.flatId === selectedFlatId)?.flatNumber} ${(flats || []).find(flat => flat.flatId === selectedFlatId)?.blockName ? `(${(flats || []).find(flat => flat.flatId === selectedFlatId)?.blockName})` : ''}`,
                flatNumber: (flats || []).find(flat => flat.flatId === selectedFlatId)?.flatNumber,
                blockName: (flats || []).find(flat => flat.flatId === selectedFlatId)?.blockName || ''
              } : 
              { flatId: '', label: t('buildingAdmin.payments.allFlats'), flatNumber: 'All', blockName: '' }
            }
            onChange={(event, newValue) => {
              const flatId = newValue ? newValue.flatId : '';
              setSelectedFlatId(flatId);
              setPaymentsPage(1); // Reset to first page when flat changes
              if (typeof window !== 'undefined') window.selectedFlatId = flatId;
              if (typeof window !== 'undefined') window.dispatchEvent(new Event('flatDropdownChange'));
            }}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.flatId === value.flatId}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const filterValue = inputValue.toLowerCase();
              return options.filter(option => 
                option.flatNumber?.toLowerCase().includes(filterValue) ||
                option.blockName?.toLowerCase().includes(filterValue) ||
                option.label?.toLowerCase().includes(filterValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('buildingAdmin.payments.selectFlat')}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            ListboxProps={{
              style: { maxHeight: 300 }
            }}
          />
          
          {/* Page size selector */}
          <FormControl sx={{ minWidth: { xs: '100%', sm: 160 }, '& .MuiInputBase-root': { height: isMobile ? 36 : 40 } }}>
            <InputLabel>{t('common.pageSize')}</InputLabel>
            <Select
              value={paymentsPageSize}
              label="Page Size"
              size={isMobile ? "small" : "medium"}
              onChange={e => {
                setPaymentsPageSize(e.target.value);
                setPaymentsPage(1); // Reset to first page when page size changes
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          
          {/* Date range selector */}
          <TextField
            label={t('common.startDate')}
            type="date"
            value={startDate}
            size={isMobile ? "small" : "medium"}
            onChange={(e) => {
              const newStartDate = e.target.value;
              const maxEndDate = new Date(newStartDate);
              maxEndDate.setMonth(maxEndDate.getMonth() + 6);
              
              setStartDate(newStartDate);
              
              // Adjust end date if it exceeds 6 months from start date
              if (new Date(endDate) > maxEndDate) {
                setEndDate(maxEndDate.toISOString().split('T')[0]);
              }
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ minWidth: 150 }}
          />
          
          <TextField
            label={t('common.endDate')}
            type="date"
            value={endDate}
            size={isMobile ? "small" : "medium"}
            onChange={(e) => {
              const newEndDate = e.target.value;
              const minStartDate = new Date(newEndDate);
              minStartDate.setMonth(minStartDate.getMonth() - 6);
              
              setEndDate(newEndDate);
              
              // Adjust start date if it's more than 6 months before end date
              if (new Date(startDate) < minStartDate) {
                setStartDate(minStartDate.toISOString().split('T')[0]);
              }
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ minWidth: 150 }}
          />
        </Box>
        {/* Payments table for selected flat */}
        {(() => {
          let filteredPayments = payments || [];
          if (selectedFlatId) {
            filteredPayments = filteredPayments.filter(p => p.flatId === selectedFlatId);
          }
          // Only show outstanding payment record for the flat if it is COMPLETED and outstandingBalance is 0
          if (selectedFlatId) {
            const flat = (flats || []).find(f => f.flatId === selectedFlatId);
            if (flat && Number(flat.outstandingBalance) === 0) {
              const outstandingPayments = payments.filter(p => p.flatId === selectedFlatId && p.paymentType === 'Outstanding' && p.status === 'COMPLETED');
              outstandingPayments.forEach(outstanding => {
                if (!filteredPayments.some(p => p.paymentId === outstanding.paymentId)) {
                  filteredPayments.push(outstanding);
                }
              });
            }
          }

          // Calculate pagination
          // Sort by most recent payment timestamp (paidDate, createdAt, dueDate) descending
          filteredPayments.sort((a, b) => (Date.parse(b.paidDate || b.createdAt || b.dueDate) || 0) - (Date.parse(a.paidDate || a.createdAt || a.dueDate) || 0));
          const totalPayments = filteredPayments.length;
          const totalPages = Math.ceil(totalPayments / paymentsPageSize);
          const startIndex = (paymentsPage - 1) * paymentsPageSize;
          const endIndex = startIndex + paymentsPageSize;
          const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

          if (filteredPayments.length === 0) {
            return (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  {t('buildingAdmin.payments.noPaymentsFoundForFlat')}
                </Typography>
              </Box>
            );
          }

          const PaymentContent = isMobile ? (
            // Mobile Card Layout
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {paginatedPayments.map(payment => {
                const flat = (flats || []).find(f => f.flatId === payment.flatId);
                let monthYear = '-';
                if (payment.paymentType === 'Outstanding' || payment.year == null || payment.month == null) {
                  monthYear = 'Outstanding';
                } else {
                  const monthName = new Date(payment.year, (payment.month || 1) - 1).toLocaleString('default', { month: 'long' });
                  monthYear = `${monthName} ${payment.year}`;
                }
                
                return (
                  <Card key={payment.paymentId} variant="outlined" sx={{ p: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                          ₹{payment.amount}
                        </Typography>
                        <Chip 
                          label={payment.status} 
                          color={payment.status === 'COMPLETED' ? 'success' : payment.status === 'PENDING' ? 'warning' : 'error'} 
                          size="small" 
                          sx={{ fontSize: '10px', height: '20px' }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                        ID: {payment.paymentId} | Flat: {flat ? flat.flatNumber : payment.flatId}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                        Type: {payment.paymentType} | Period: {monthYear}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '11px' }}>
                          Due: {payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '11px' }}>
                          Paid: {payment.paidDate ? new Date(payment.paidDate).toLocaleDateString() : '-'}
                        </Typography>
                      </Box>
                      {payment.description && (
                        <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                          {payment.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            // Desktop Table Layout
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('buildingAdmin.payments.paymentId')}</TableCell>
                    <TableCell>{t('buildingAdmin.flats.flatNumber')}</TableCell>
                    <TableCell>{t('buildingAdmin.payments.amount')}</TableCell>
                    <TableCell>{t('buildingAdmin.payments.paymentType')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell>{t('buildingAdmin.payments.monthYear')}</TableCell>
                    <TableCell>{t('buildingAdmin.payments.dueDate')}</TableCell>
                    <TableCell>{t('buildingAdmin.payments.paidDate')}</TableCell>
                    <TableCell>{t('buildingAdmin.payments.description')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedPayments.map(payment => {
                    const flat = (flats || []).find(f => f.flatId === payment.flatId);
                    let monthYear = '-';
                    if (payment.paymentType === 'Outstanding' || payment.year == null || payment.month == null) {
                      monthYear = 'Outstanding';
                    } else {
                      const monthName = new Date(payment.year, (payment.month || 1) - 1).toLocaleString('default', { month: 'long' });
                      monthYear = `${monthName} ${payment.year}`;
                    }
                    return (
                      <TableRow key={payment.paymentId}>
                        <TableCell>{payment.paymentId}</TableCell>
                        <TableCell>{flat ? flat.flatNumber : payment.flatId}</TableCell>
                        <TableCell>₹{payment.amount}</TableCell>
                        <TableCell>{payment.paymentType}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              bgcolor: payment.status === 'COMPLETED' ? '#e8f5e8' : 
                                       payment.status === 'PENDING' ? '#fff3e0' : '#ffebee',
                              color: payment.status === 'COMPLETED' ? '#2e7d32' : 
                                     payment.status === 'PENDING' ? '#f57c00' : '#d32f2f'
                            }}
                          >
                            {payment.status}
                          </Box>
                        </TableCell>
                        <TableCell>{monthYear}</TableCell>
                        <TableCell>
                          {payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          {payment.paidDate ? new Date(payment.paidDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>{payment.description || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          );

          return (
            <Box>
              {PaymentContent}
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mt: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 2
                }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.showingResults', { 
                      start: startIndex + 1, 
                      end: Math.min(endIndex, totalPayments), 
                      total: totalPayments 
                    })}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={paymentsPage}
                    onChange={(event, value) => setPaymentsPage(value)}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          );
        })()}
      </TabPanel>



      {/* Payment Confirmation Dialog */}
      <Dialog 
        open={showPaymentConfirmation} 
        onClose={() => setShowPaymentConfirmation(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Confirm Partial Payment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {paymentConfirmationMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentConfirmation(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowPaymentConfirmation(false);
              const method = selectedFlatForPayment?._paymentMethod || 'Cash';
              const amount = selectedFlatForPayment?._paymentAmount || Number(paymentAmount);
              
              if (method === 'Razorpay') {
                handleAdminRazorpayPayment(selectedFlatForPayment, amount, 'Online');
              } else if (method === 'CCAvenue') {
                setCcavenuePaymentAmount(prev => ({
                  ...prev,
                  [selectedFlatForPayment.flatId]: amount
                }));
                toggleCCAvCard(selectedFlatForPayment.flatId);
              } else {
                processPayment(selectedFlatForPayment, amount);
              }
            }}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default BuildingAdminDashboard;